import React, { useMemo } from 'react';
import { Box, Inscription, Preloader, useResponsive } from '@common-fe/common-fe';

import BeneficiariesList from './BeneficiariesList';
import SpecialStatesWarning from './SpecialStatesWarning';
import useGetBeneficiariesQuery, { BeneficiaryType } from './useGetBeneficiaries.query';
import useGetBeneficiaryPaperFormLink from './useGetBeneficiaryPaperFormLink';
import useHasWarningAddressState from './useHasWarningAddressState';

const Beneficiaries = () => {
  const { isMobile } = useResponsive();
  const beneficiaryPaperFormLink = useGetBeneficiaryPaperFormLink();
  const hasWarningState = useHasWarningAddressState();
  const { isLoading, formattedData } = useGetBeneficiariesQuery();
  const [primaryBeneficiaries, contingentBeneficiaries] = useMemo(
    () => [
      formattedData?.filter((item) => item?.beneficiaryType === BeneficiaryType.PRIMARY),
      formattedData?.filter((item) => item?.beneficiaryType === BeneficiaryType.CONTINGENT),
    ],
    [formattedData]
  );
  const hasContingentBeneficiary = useMemo(
    () => Boolean(contingentBeneficiaries?.length),
    [contingentBeneficiaries]
  );

  return (
    <Box data-testId="Beneficiaries">
      {primaryBeneficiaries?.length && hasWarningState ? (
        <Box margin={{ bottom: 'spacing24' }}>
          <SpecialStatesWarning />
        </Box>
      ) : null}
      {isLoading ? (
        <Preloader />
      ) : (
        <Box>
          <BeneficiariesList
            beneficiaries={primaryBeneficiaries}
            beneficiaryType={BeneficiaryType.PRIMARY}
            isRemovingAllPrimaryBeneficiariesButtonHidden={hasContingentBeneficiary}
          />

          <Box margin={{ top: 'spacing12' }}>
            <BeneficiariesList
              beneficiaries={contingentBeneficiaries}
              beneficiaryType={BeneficiaryType.CONTINGENT}
              isAddButtonDisabled={!primaryBeneficiaries?.length}
            />
          </Box>

          <Box
            margin={isMobile ? { top: 'spacing12' } : { top: 'spacing24' }}
            pad={isMobile ? { horizontal: 'spacing16' } : undefined}
          >
            <Inscription color="textSecondary" size="small" weight={500} lineHeight="18px">
              Please review and update the beneficiary details associated with your Health Savings Account
              (HSA). The individuals you designate will inherit your HSA funds upon your passing, and this
              designation remains effective until formally revised. As these choices may have tax or legal
              implications, we strongly advise consulting with your legal or tax advisor before making any
              changes.
            </Inscription>
          </Box>

          {beneficiaryPaperFormLink ? (
            <Box margin={{ top: 'spacing24' }} pad={isMobile ? { horizontal: 'spacing16' } : undefined}>
              <Inscription color="textSecondary" size="small" weight="bold" lineHeight="18px">
                Beneficiary paper form
                <Inscription weight="normal" size="small">
                  &nbsp;can be found&nbsp;
                </Inscription>
                <Inscription
                  weight="normal"
                  size="small"
                  color="textAccent"
                  onClick={() => window.open(beneficiaryPaperFormLink, '_blank')}
                  cursor="pointer"
                >
                  here.
                </Inscription>
              </Inscription>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default Beneficiaries;
